import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import {
  NanaeHeader,
  NanaeFooter,
  QSectionHeader,
} from "@/components/qv3";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { track } from "@/lib/track";
import { AccueilVoixPlayer } from "@/components/qorum/AccueilVoixPlayer";
import { OnboardingFlow } from "@/components/qorum/OnboardingFlow";
import {
  SaisineAttachments,
  type PendingItem,
  type SaisineAttachmentsHandle,
} from "@/components/qorum/SaisineAttachments";
import {
  getCheckoutStash,
  clearCheckoutStash,
  markJustPaid,
} from "@/lib/checkout-stash";
import { BranchSelector } from "@/components/qorum/BranchSelector";
import { TendancesModeSelector } from "@/components/qorum/TendancesModeSelector";
import { AuditModeSelector } from "@/components/qorum/AuditModeSelector";
import { BusinessModeSelector } from "@/components/qorum/BusinessModeSelector";
import {
  TENDANCES_ENABLED,
  AUDIT_ENABLED,
  VERIF_ENABLED,
  DEFAULT_TENDANCES_MODE,
  DEFAULT_AUDIT_MODE,
  DEFAULT_BUSINESS_MODE,
  type Branch,
  type TendancesMode,
  type AuditMode,
  type BusinessMode,
} from "@/lib/branches";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/saisine")({
  head: () => ({
    meta: [
      { title: "Nouvelle saisine · Hōjin" },
      {
        name: "description",
        content: "Soumettez votre question stratégique au Conseil. L'Antichambre l'affinera avant la délibération.",
      },
      { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Nouvelle saisine · Hōjin",
        description: "Soumettez votre question stratégique au Conseil. L'Antichambre l'affinera avant la délibération.",
        url: "https://hojin.ai/saisine",
        image: "maieute",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/saisine" }],
  }),
  component: () => (
    <ProtectedRoute>
      <SaisinePage />
    </ProtectedRoute>
  ),
});

const MODES = [
  { value: "court", label: "Court" },
  { value: "standard", label: "Standard" },
  { value: "long", label: "Long" },
] as const;

const SCRAMBLE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!?*+~/\\<>";
const randChar = () => SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];

function scrambleText(source: string, progress: number): string {
  // progress 0 → fully scrambled; 1 → fully resolved to source
  const reveal = Math.floor(source.length * progress);
  let out = "";
  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (i < reveal || ch === " " || ch === "\n") out += ch;
    else out += randChar();
  }
  return out;
}

function SaisinePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("checkout") !== "success") return;

    // Toujours nettoyer le param d'URL
    url.searchParams.delete("checkout");
    url.searchParams.delete("session_id");
    window.history.replaceState(
      {},
      "",
      url.pathname + (url.search ? url.search : "") + url.hash,
    );

    // Si l'user payait depuis un débat existant → on le ramène à son débat.
    // Le RelaunchComposer hydratera la question depuis le stash et nettoiera.
    const stash = getCheckoutStash();
    if (stash?.convId) {
      markJustPaid();
      navigate({
        to: "/deliberation/$id",
        params: { id: stash.convId },
        replace: true,
      });
      return;
    }

    // Fallback (stash absent ou paiement pour une nouvelle saisine). La question composée est RESTAURÉE
    // par l'initialiseur paresseux de `question` ci-dessous (lu AVANT ce clear) → jamais de re-saisie.
    setCheckoutSuccess(true);
    toast.success("Paiement confirmé — votre débat est crédité. Reprenez là où vous en étiez.");
    clearCheckoutStash();
    const t = setTimeout(() => setCheckoutSuccess(false), 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Retour d'un paiement pour une NOUVELLE saisine (pas de convId) : on ré-hydrate la question composée depuis
  // le stash — l'initialiseur s'exécute au 1er rendu, AVANT que l'effet ci-dessus ne vide le stash. Zéro re-saisie.
  const [question, setQuestion] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get("checkout") === "success") {
        const s = getCheckoutStash();
        if (s && !s.convId && s.pendingQuestion) return s.pendingQuestion;
      }
    } catch { /* ignore */ }
    return "";
  });
  const [branch, setBranch] = useState<Branch>("business");
  const [tendancesMode, setTendancesMode] = useState<TendancesMode>(
    DEFAULT_TENDANCES_MODE,
  );
  const [auditMode, setAuditMode] = useState<AuditMode>(DEFAULT_AUDIT_MODE);
  const [businessMode, setBusinessMode] = useState<BusinessMode>(
    DEFAULT_BUSINESS_MODE,
  );
  const [duree, setDuree] = useState<(typeof MODES)[number]["value"]>("standard");
  const [error, setError] = useState<string | null>(null);
  const [sharpening, setSharpening] = useState(false);
  const [previousQuestion, setPreviousQuestion] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<PendingItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const attachmentsRef = useRef<SaisineAttachmentsHandle>(null);
  const rafRef = useRef<number | null>(null);
  const trimmed = question.trim();
  const uploading = attachments.some((a) => a.status === "uploading");
  const canSubmit = trimmed.length >= 12 && !uploading;
  const canSharpen = branch !== "audit" && branch !== "verif" && trimmed.length >= 15 && !sharpening;

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const animateTo = (target: string, durationMs: number) =>
    new Promise<void>((resolve) => {
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / durationMs);
        setQuestion(scrambleText(target, p));
        if (p < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setQuestion(target);
          resolve();
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    });

  const animateScrambleLoop = (source: string, stop: { current: boolean }) =>
    new Promise<void>((resolve) => {
      const tick = () => {
        if (stop.current) return resolve();
        setQuestion(scrambleText(source, 0));
        rafRef.current = requestAnimationFrame(() => {
          setTimeout(tick, 60);
        });
      };
      tick();
    });

  const handleSharpen = async () => {
    if (branch === "audit" || branch === "verif") return;
    if (!canSharpen) return;
    const original = question;
    setPreviousQuestion(original);
    setSharpening(true);

    const stop = { current: false };
    // Loop scramble during network call
    void animateScrambleLoop(original, stop);

    try {
      const res = await apiFetch<{ sharpened?: string }>("/api/sharpen", {
        method: "POST",
        body: JSON.stringify({ question: original }),
      });
      stop.current = true;
      const sharpened = (res?.sharpened ?? "").trim();
      const target = sharpened.length > 0 ? sharpened : original;
      await animateTo(target, 700);
    } catch {
      stop.current = true;
      // silent: reform on original
      await animateTo(original, 500);
      setPreviousQuestion(null);
    } finally {
      setSharpening(false);
    }
  };

  const revertSharpen = () => {
    if (previousQuestion == null) return;
    setQuestion(previousQuestion);
    setPreviousQuestion(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      setError(
        uploading
          ? "Patientez : un document est encore en cours d'envoi."
          : "Précisez votre question (minimum 12 caractères).",
      );
      return;
    }
    // Garde-fou : tant que Tendances n'est pas activé côté backend, on n'émet rien.
    if (branch === "tendances" && !TENDANCES_ENABLED) {
      setError("La branche Tendances arrive bientôt.");
      return;
    }
    if (branch === "audit" && !AUDIT_ENABLED) {
      setError("La branche Audit arrive bientôt.");
      return;
    }
    if (branch === "verif" && !VERIF_ENABLED) {
      setError("La branche Audit arrive bientôt.");
      return;
    }
    setError(null);

    const manifest = attachments
      .filter((a) => a.status === "done" && a.manifest)
      .map((a) => a.manifest!);
    const isTendances = branch === "tendances" && TENDANCES_ENABLED;
    const isAudit = branch === "audit" && AUDIT_ENABLED;
    const isVerif = branch === "verif" && VERIF_ENABLED;
    navigate({
      to: "/antichambre",
      search: {
        question: trimmed,
        mode: isVerif
          ? "verif"
          : isAudit
            ? auditMode
            : isTendances
              ? tendancesMode
              : businessMode,
        branch,
        ...(isTendances ? { tendances_mode: tendancesMode } : {}),
        ...(isAudit ? { audit_mode: auditMode } : {}),
        
        ...(manifest.length > 0 ? { manifest } : {}),
      },
    });
  };

  return (
    <div className="nanae-page qv3-dark saisine-light-mode">
      <OnboardingFlow />
      <AccueilVoixPlayer />
      <style>{`
        @keyframes sharpen-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 239, 227, 0); }
          50% { box-shadow: 0 0 18px 2px rgba(245, 239, 227, 0.35); }
        }
        .sharpen-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: transparent;
          border: 1px solid rgba(245, 239, 227, 0.45);
          color: #F5EFE3;
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
        }
        .sharpen-btn:not(:disabled):hover {
          background: rgba(245, 239, 227, 0.08);
          border-color: #F5EFE3;
          color: #FFFFFF;
        }
        .sharpen-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .sharpen-btn.is-ready {
          animation: sharpen-pulse 2.4s ease-in-out infinite;
        }
        .sharpen-btn.is-busy {
          color: #FFFFFF;
          border-color: #F5EFE3;
        }
        .sharpen-revert {
          background: none;
          border: none;
          color: var(--qv3-grey-500, #8a8580);
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-size: 11px;
          letter-spacing: 0.06em;
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .sharpen-revert:hover { color: #F5EFE3; }
        .sharpen-textarea-busy {
          color: #F5EFE3 !important;
          text-shadow: 0 0 6px rgba(245, 239, 227, 0.45);
          transition: text-shadow 0.2s;
        }
        .saisine-drop-wrap { position: relative; }
        .saisine-drop-wrap.is-drag .nanae-textarea {
          border-color: #F5EFE3;
          box-shadow: 0 0 0 2px rgba(245, 239, 227, 0.25);
          transition: box-shadow 0.15s, border-color 0.15s;
        }
        .saisine-drop-hint {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          pointer-events: none;
          background: rgba(245, 239, 227, 0.06);
          color: #F5EFE3;
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
          border-radius: 2px;
        }

        /* ——— Mode clair scopé à la page saisine ——— */
        .saisine-light-mode {
          /* Redéfinir les tokens qv3 dans la portée light */
          --qv3-paper: #000000;
          --qv3-ink: #000000;
          --qv3-bg: #FFFFFF;
          --qv3-grey-500: #444444;
          --qv3-grey-400: #666666;
          --qv3-grey-300: #888888;
          --nacre: #000000;
          background: #FFFFFF !important;
          color: #000000 !important;
        }
        /* Ne pas contaminer le header/footer sur fond noir */
        .saisine-light-mode .nanae-header,
        .saisine-light-mode .nanae-footer,
        .saisine-light-mode .nanae-header *,
        .saisine-light-mode .nanae-footer * {
          color: revert !important;
          background: revert;
        }
        .saisine-light-mode .nanae-header {
          background: #000000 !important;
        }
        .saisine-light-mode .nanae-header a,
        .saisine-light-mode .nanae-header span,
        .saisine-light-mode .nanae-header button,
        .saisine-light-mode .nanae-header .nanae-wordmark {
          color: #F5EFE3 !important;
        }
        .saisine-light-mode .nanae-page-main {
          background: #FFFFFF !important;
          color: #000000 !important;
        }
        .saisine-light-mode .qv3-h2,
        .saisine-light-mode h1,
        .saisine-light-mode h2,
        .saisine-light-mode h3,
        .saisine-light-mode h4,
        .saisine-light-mode label,
        .saisine-light-mode .nanae-form-label,
        .saisine-light-mode .qv3-body,
        .saisine-light-mode p,
        .saisine-light-mode span,
        .saisine-light-mode li,
        .saisine-light-mode a,
        .saisine-light-mode strong,
        .saisine-light-mode em,
        .saisine-light-mode .qv3-mono-hud {
          color: #000000 !important;
        }
        .saisine-light-mode .nanae-textarea,
        .saisine-light-mode input,
        .saisine-light-mode textarea,
        .saisine-light-mode select {
          background: #FFFFFF !important;
          color: #000000 !important;
          border-color: #000000 !important;
        }
        .saisine-light-mode .nanae-textarea::placeholder,
        .saisine-light-mode textarea::placeholder,
        .saisine-light-mode input::placeholder {
          color: #666666 !important;
        }
        /* Cadres génériques — cartes, sélecteurs, options */
        .saisine-light-mode [class*="card"],
        .saisine-light-mode [class*="Card"],
        .saisine-light-mode [class*="option"],
        .saisine-light-mode [class*="Option"],
        .saisine-light-mode [class*="selector"],
        .saisine-light-mode [class*="Selector"],
        .saisine-light-mode fieldset,
        .saisine-light-mode .nanae-form-row,
        .saisine-light-mode .nanae-saisine-options {
          background: #FFFFFF !important;
          border-color: rgba(0,0,0,0.35) !important;
          color: #000000 !important;
        }
        /* État sélectionné : fond noir, texte blanc */
        .saisine-light-mode [aria-checked="true"],
        .saisine-light-mode [aria-selected="true"],
        .saisine-light-mode [data-state="checked"],
        .saisine-light-mode [data-selected="true"],
        .saisine-light-mode .is-selected,
        .saisine-light-mode .is-active {
          background: #000000 !important;
          color: #FFFFFF !important;
          border-color: #000000 !important;
        }
        .saisine-light-mode [aria-checked="true"] *,
        .saisine-light-mode [aria-selected="true"] *,
        .saisine-light-mode [data-state="checked"] *,
        .saisine-light-mode [data-selected="true"] *,
        .saisine-light-mode .is-selected *,
        .saisine-light-mode .is-active * {
          color: #FFFFFF !important;
        }
        /* Radios durée */
        .saisine-light-mode .nanae-radio-group label {
          border: 1px solid rgba(0,0,0,0.35) !important;
          color: #000000 !important;
          background: #FFFFFF !important;
        }
        .saisine-light-mode .nanae-radio-group input:checked + * ,
        .saisine-light-mode .nanae-radio-group label:has(input:checked) {
          background: #000000 !important;
          color: #FFFFFF !important;
          border-color: #000000 !important;
        }
        .saisine-light-mode .sharpen-btn {
          border-color: #000000 !important;
          color: #000000 !important;
          background: #FFFFFF !important;
        }
        .saisine-light-mode .sharpen-btn:not(:disabled):hover {
          background: #000000 !important;
          color: #FFFFFF !important;
          border-color: #000000 !important;
        }
        .saisine-light-mode .sharpen-revert { color: #444444 !important; }
        .saisine-light-mode .sharpen-revert:hover { color: #000000 !important; }
        .saisine-light-mode .nanae-cta-large {
          background: #000000 !important;
          color: #FFFFFF !important;
          border-color: #000000 !important;
        }
        .saisine-light-mode .nanae-cta-large *,
        .saisine-light-mode .nanae-cta-large span,
        .saisine-light-mode .nanae-cta-large em {
          color: #FFFFFF !important;
        }
        .saisine-light-mode .nanae-cta-large:hover:not(:disabled) {
          background: #1a1a1a !important;
        }
        /* SVG icônes */
        .saisine-light-mode svg { color: #000000; }

        /* ——— Sélecteurs à styles inline (BranchSelector, BusinessMode, etc.) ——— */
        .saisine-light-mode fieldset[data-testid="branch-selector"] button[role="radio"],
        .saisine-light-mode div[role="radiogroup"] button[role="radio"] {
          background: #FFFFFF !important;
          border: 1px solid rgba(0,0,0,0.4) !important;
          color: #000000 !important;
        }
        .saisine-light-mode fieldset[data-testid="branch-selector"] button[role="radio"] *,
        .saisine-light-mode div[role="radiogroup"] button[role="radio"] * {
          color: #000000 !important;
        }
        .saisine-light-mode fieldset[data-testid="branch-selector"] button[role="radio"][aria-checked="true"],
        .saisine-light-mode div[role="radiogroup"] button[role="radio"][aria-checked="true"] {
          background: #000000 !important;
          border-color: #000000 !important;
          color: #FFFFFF !important;
        }
        .saisine-light-mode fieldset[data-testid="branch-selector"] button[role="radio"][aria-checked="true"] *,
        .saisine-light-mode div[role="radiogroup"] button[role="radio"][aria-checked="true"] * {
          color: #FFFFFF !important;
        }
        .saisine-light-mode fieldset[data-testid="branch-selector"] button[role="radio"][aria-disabled="true"] {
          opacity: 0.5 !important;
          color: #666666 !important;
        }
        .saisine-light-mode legend { color: #000000 !important; }

        /* Pièces jointes */
        .saisine-light-mode .saisine-attach-btn {
          color: #000000 !important;
        }
        .saisine-light-mode .saisine-attach-chip {
          background: #FFFFFF !important;
          border: 1px solid rgba(0,0,0,0.35) !important;
          color: #000000 !important;
        }
        .saisine-light-mode .saisine-attach-chip * { color: #000000 !important; }

        /* ——— Filet de sécurité : tous les boutons non-CTA en noir sur blanc ——— */
        .saisine-light-mode button:not(.nanae-cta-large):not(.sharpen-btn):not([aria-checked="true"]) {
          color: #000000 !important;
          background-color: #FFFFFF !important;
          border-color: rgba(0,0,0,0.4) !important;
        }
        .saisine-light-mode button:not(.nanae-cta-large):not(.sharpen-btn):not([aria-checked="true"]) *,
        .saisine-light-mode button:not(.nanae-cta-large):not(.sharpen-btn):not([aria-checked="true"]) span,
        .saisine-light-mode button:not(.nanae-cta-large):not(.sharpen-btn):not([aria-checked="true"]) em {
          color: #000000 !important;
        }
        .saisine-light-mode button[aria-checked="true"],
        .saisine-light-mode button[aria-checked="true"] * {
          color: #FFFFFF !important;
        }
        .saisine-light-mode button[aria-checked="true"] {
          background-color: #000000 !important;
          border-color: #000000 !important;
        }
        /* Filet de sécurité texte : tout ce qui n'est pas dans un CTA doit être noir */
        .saisine-light-mode .nanae-page-main *:not(.nanae-cta-large):not(.nanae-cta-large *) {
          --qv3-paper: #000000;
          --nacre: #000000;
        }
        /* Bouton Affûter — noir plein, texte blanc, pictogramme rouge fluo */
        .saisine-light-mode .sharpen-btn,
        .saisine-light-mode .sharpen-btn.is-ready,
        .saisine-light-mode .sharpen-btn.is-busy {
          color: #FFFFFF !important;
          background: #000000 !important;
          border: 1px solid #000000 !important;
          box-shadow: none !important;
          animation: none !important;
          text-shadow: none !important;
          font-weight: 600 !important;
          opacity: 1 !important;
        }
        .saisine-light-mode .sharpen-btn *,
        .saisine-light-mode .sharpen-btn span {
          color: #FFFFFF !important;
        }
        .saisine-light-mode .sharpen-btn .sharpen-icon {
          color: #FF1744 !important;
          text-shadow: 0 0 8px rgba(255, 23, 68, 0.75) !important;
        }
        .saisine-light-mode .sharpen-btn:not(:disabled):hover {
          background: #111111 !important;
          color: #FFFFFF !important;
        }
        .saisine-light-mode .sharpen-textarea-busy {
          color: #000000 !important;
          text-shadow: none !important;
        }
      `}</style>

      <NanaeHeader
        variant="minimal"
        user={{
          email: user?.email,
          name:
            (user?.user_metadata as { first_name?: string } | undefined)?.first_name ??
            user?.email,
        }}
      />

      <main className="nanae-page-main is-narrow">
        {checkoutSuccess && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginBottom: 24,
              padding: "14px 18px",
              border: "0.5px solid var(--qv3-gold)",
              background: "rgba(212, 165, 116, 0.08)",
              color: "var(--qv3-paper)",
              fontFamily: "var(--qv3-font-mono)",
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Paiement reçu — votre débat est crédité. Saisissez le Conseil.
          </div>
        )}
        <QSectionHeader number="I" title="NOUVELLE SAISINE" />

        <h1
          className="qv3-h2"
          style={{
            fontStyle: "italic",
            color: "var(--qv3-paper)",
            marginTop: 24,
            marginBottom: 16,
          }}
        >
          Que souhaitez-vous soumettre au Conseil ?
        </h1>
        <p
          className="qv3-body"
          style={{ color: "var(--qv3-grey-500)", marginBottom: 40, fontStyle: "italic" }}
        >
          Soyez clair, soyez bref. Le Conseil affinera votre question.
        </p>

        <form onSubmit={(e) => { track("Début saisine", { branche: branch }); submit(e); }} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <BranchSelector value={branch} onChange={setBranch} />
          {branch === "business" && (
            <BusinessModeSelector
              value={businessMode}
              onChange={setBusinessMode}
            />
          )}
          {branch === "tendances" && TENDANCES_ENABLED && (
            <TendancesModeSelector
              value={tendancesMode}
              onChange={setTendancesMode}
            />
          )}
          {branch === "audit" && AUDIT_ENABLED && (
            <AuditModeSelector value={auditMode} onChange={setAuditMode} />
          )}
          <div className="nanae-form-row">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <label htmlFor="saisine-question" className="nanae-form-label">
                QUESTION STRATÉGIQUE
              </label>
              {branch !== "audit" && branch !== "verif" && (
                <button
                  type="button"
                  onClick={handleSharpen}
                  disabled={!canSharpen}
                  className={`sharpen-btn ${canSharpen ? "is-ready" : ""} ${sharpening ? "is-busy" : ""}`}
                  aria-label="Affûter ma question"
                >
                  <span className="sharpen-icon" aria-hidden="true">✦</span>
                  <span>{sharpening ? "Affûtage…" : "Affûter ma question"}</span>
                </button>
              )}
            </div>
            <div
              className={`saisine-drop-wrap ${dragOver ? "is-drag" : ""}`}
              onDragOver={(e) => {
                if (e.dataTransfer?.types?.includes("Files")) {
                  e.preventDefault();
                  setDragOver(true);
                }
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setDragOver(false);
              }}
              onDrop={(e) => {
                if (e.dataTransfer?.files?.length) {
                  e.preventDefault();
                  setDragOver(false);
                  attachmentsRef.current?.addFiles(e.dataTransfer.files);
                }
              }}
            >
              <textarea
                id="saisine-question"
                className={`nanae-textarea ${sharpening ? "sharpen-textarea-busy" : ""}`}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onPaste={(e) => {
                  const files = Array.from(e.clipboardData?.files ?? []);
                  if (files.length > 0) {
                    e.preventDefault();
                    attachmentsRef.current?.addFiles(files);
                  }
                }}
                placeholder="Saisissez votre question stratégique…"
                autoFocus
                maxLength={2000}
                readOnly={sharpening}
              />
              {dragOver && (
                <div className="saisine-drop-hint" aria-hidden="true">
                  Déposez ici
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              {previousQuestion != null && !sharpening ? (
                <button type="button" onClick={revertSharpen} className="sharpen-revert">
                  ↺ revenir à ma version
                </button>
              ) : (
                <span />
              )}
              <span
                className="qv3-mono-hud size-xs"
                style={{ textAlign: "right", opacity: 0.6 }}
              >
                {question.length}/2000
              </span>
            </div>
            <SaisineAttachments
              ref={attachmentsRef}
              userId={user?.id ?? null}
              items={attachments}
              onChange={setAttachments}
            />
          </div>


          <div className="nanae-saisine-options">
            <div className="nanae-form-row" style={{ gap: 14 }}>
              <span className="nanae-form-label">DURÉE SOUHAITÉE</span>
              <div className="nanae-radio-group">
                {MODES.map((m) => (
                  <label key={m.value}>
                    <input
                      type="radio"
                      name="duree"
                      value={m.value}
                      checked={duree === m.value}
                      onChange={() => setDuree(m.value)}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <p
              role="alert"
              className="qv3-mono-hud size-sm"
              style={{ color: "var(--qv3-led-red)" }}
            >
              {error}
            </p>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 24,
              marginTop: 16,
              flexWrap: "wrap",
            }}
          >
            <button type="submit" className="nanae-cta-large" disabled={!canSubmit || sharpening}>
              Soumettre au Conseil →
            </button>
          </div>
        </form>
      </main>

      <NanaeFooter variant="minimal" />
    </div>
  );
}
